import { datadogRum } from '@datadog/browser-rum';
import useGridStore from '@/publicSearch/store/useGridStore';

function rumReady(): boolean {
  return typeof window !== 'undefined' && !!datadogRum.getInitConfiguration();
}

function addAction(name: string, context: Record<string, unknown>): void {
  if (!rumReady()) return;
  datadogRum.addAction(name, context);
}

/**
 * Subscribes to grid store updates after persist hydration so restore does not emit actions.
 * Returns a teardown that unsubscribes from hydration + store listener.
 */
export function subscribePublicSearchGridStoreAfterHydration(): () => void {
  let storeUnsub: (() => void) | undefined;

  const unsubHydration = useGridStore.persist.onFinishHydration(() => {
    storeUnsub?.();
    storeUnsub = useGridStore.subscribe((state, prev) => {
      if (
        JSON.stringify(state.gridSortModel) !==
        JSON.stringify(prev.gridSortModel)
      ) {
        const first = state.gridSortModel[0];
        addAction('Grid: Sort Changed', {
          field: first?.field ?? null,
          sort: first?.sort ?? null,
        });
      }

      if (
        state.paginationModel.page !== prev.paginationModel.page ||
        state.paginationModel.pageSize !== prev.paginationModel.pageSize
      ) {
        addAction('Grid: Pagination', {
          page: state.paginationModel.page,
          pageSize: state.paginationModel.pageSize,
        });
      }

      if (
        JSON.stringify(state.columnVisibilityModel) !==
        JSON.stringify(prev.columnVisibilityModel)
      ) {
        const visible = Object.values(state.columnVisibilityModel).filter(
          (v) => v !== false,
        ).length;
        addAction('Grid: Column Visibility', {
          visibleColumnCount: visible,
        });
      }

      if (state.myAnimals !== prev.myAnimals) {
        addAction('Grid: My Herd Filter', { enabled: state.myAnimals });
      }

      if (state.animalsOnSaleOnly !== prev.animalsOnSaleOnly) {
        addAction('Grid: Active Sale Listings Filter', {
          enabled: state.animalsOnSaleOnly,
        });
      }
    });
  });

  return () => {
    unsubHydration();
    storeUnsub?.();
  };
}
